import { useState, type CSSProperties } from "react";

/**
 * Sprint 14g — shared bits for the unified archive UX across cockpit panels:
 * the "Show archived" filter chip, the amber "Archived" row badge, and the
 * per-panel localStorage persistence hook. Centralised so all 8 panels share
 * one styling + storage convention.
 */

const storageKey = (panelId: string, scopeId: string) =>
  `kuvio_show_archived_${panelId}_${scopeId}`;

/**
 * Boolean "show archived" state, persisted per panel + scope (node/workspace id)
 * in localStorage. Initialised once on mount (mirrors the existing per-panel
 * filter pattern) — panels remount when the active node changes.
 */
export function useShowArchived(panelId: string, scopeId: string): [boolean, (v: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey(panelId, scopeId)) === "1";
    } catch {
      return false;
    }
  });
  const set = (v: boolean) => {
    setValue(v);
    try {
      localStorage.setItem(storageKey(panelId, scopeId), v ? "1" : "0");
    } catch {
      /* storage disabled — keep in-memory only */
    }
  };
  return [value, set];
}

const chipStyle: CSSProperties = {
  fontSize: 12,
  padding: "4px 8px",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  whiteSpace: "nowrap",
};

const chipActiveStyle: CSSProperties = {
  background: "var(--color-background-secondary, rgba(0,0,0,0.04))",
  color: "var(--color-text-primary)",
  borderColor: "var(--color-border-primary, var(--color-border-secondary))",
};

export function ShowArchivedChip({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      title={value ? "Hide archived items" : "Show archived items"}
      aria-pressed={value}
      style={{ ...chipStyle, ...(value ? chipActiveStyle : {}) }}
    >
      <i className="ti ti-archive" style={{ fontSize: 13 }} aria-hidden="true" />
      {value ? "Hide archived" : "Show archived"}
    </button>
  );
}

const badgeStyle: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 0.2,
  textTransform: "uppercase",
  padding: "1px 6px",
  borderRadius: 999,
  color: "var(--c-amber-700, #b45309)",
  background: "var(--c-amber-50, rgba(245, 158, 11, 0.12))",
  border: "0.5px solid var(--c-amber-200, rgba(245, 158, 11, 0.3))",
  flexShrink: 0,
};

export function ArchivedBadge() {
  return <span style={badgeStyle}>Archived</span>;
}
